<template>
  <RevDrawer
    :name="MODAL_NAMES.VERIFIED_REFURBISHED_CAROUSEL"
    :title="i18n(translations.modalTitle)"
    @open="onOpen"
  >
    <template #body>
      <MarketingVideo
        v-if="videoSource.isSelfHosted"
        class="!h-[187px]"
        img-class-name="aspect-video"
        :is-mobile="true"
        :poster="videoSource.poster"
        :product-name
        :sources="videoSource.sources"
        :title="videoSource.title"
      />
      <YoutubeVideo
        v-else
        class="!h-[187px]"
        img-class-name="aspect-video"
        :is-mobile="true"
      />

      <h2 class="heading-2 mt-24 text-center">
        {{ checkpoints.subtitle }}
      </h2>

      <RevDivider class="mt-24" />

      <RevAccordionList :has-external-borders="false">
        <RevAccordionItem
          v-for="(checkpoint, index) in checkpoints.items"
          :key="checkpoint.text"
          :ref="
            (el) => {
              if (el) accordionItemRefs[index] = el as { $el: HTMLElement }
            }
          "
          :collapsed="index === forcedExpandIndex ? 'expanded' : 'auto'"
          @click="onCheckpointClick(index)"
        >
          <template #prefix>
            <DynamicIcon :icon="checkpoint.iconToken" size="medium" />
          </template>
          <template #title>
            {{ checkpoint.text }}
          </template>
          <template #body>
            {{ checkpoint.description }}
          </template>
        </RevAccordionItem>
      </RevAccordionList>
    </template>
  </RevDrawer>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue'

import { RevAccordionItem } from '@backmarket/design-system/components/AccordionItem/index'
import { RevAccordionList } from '@backmarket/design-system/components/AccordionList/index'
import DynamicIcon from '@backmarket/nuxt-layer-cms/DynamicIcon.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevDivider } from '@ds/components/Divider'
import { RevDrawer } from '@ds/components/Drawer'

import type { GetVerifiedRefurbishCarouselResponse } from '~/api/product-page/types'
import YoutubeVideo from '~/scopes/product-core/components/presentationals/GalleryNew/components/YoutubeVideo/YoutubeVideo.vue'
import MarketingVideo from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryVideoItem/components/MarketingVideo/MarketingVideo.vue'
import { useGalleryVideoSource } from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryVideoItem/components/MarketingVideo/composables/useGalleryVideoSource'
import { useFetchProduct } from '~/scopes/product-core/composables/useFetch/useFetchProduct/useFetchProduct'
import { MODAL_NAMES } from '~/scopes/product-core/constants'

import translations from './VerifiedRefurbishedCarouselModal.translations'

const props = defineProps<{
  checkpoints: GetVerifiedRefurbishCarouselResponse
  openedIndex: number
}>()

const i18n = useI18n()

const videoSource = useGalleryVideoSource()

const { data: productResponse } = useFetchProduct()
const productName = computed(() => productResponse.value?.model)

const forcedExpandIndex = ref(-1)
const accordionItemRefs = ref<{ $el: HTMLElement }[]>([])

function onCheckpointClick(index: number) {
  if (index === forcedExpandIndex.value) {
    forcedExpandIndex.value = -1
  }
}

function onOpen() {
  forcedExpandIndex.value = props.openedIndex
  nextTick().then(() => {
    accordionItemRefs.value[props.openedIndex]?.$el.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  })
}

watch(
  () => props.openedIndex,
  (newIndex) => {
    forcedExpandIndex.value = newIndex
  },
)
</script>
